// API Configuration
// This file centralizes all API endpoint configurations

// Get API base URL from environment variable or use default
const getApiBaseUrl = () => {
  // Check for environment variable first
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Fallback to default production URL
  return 'https://5l7ywi8ha8.execute-api.us-east-1.amazonaws.com';
};

// Get S3 bucket URL from environment variable or use default
const getS3BucketUrl = () => {
  // Check for environment variable first
  if (process.env.REACT_APP_S3_BUCKET_URL) {
    return process.env.REACT_APP_S3_BUCKET_URL;
  }
  // Fallback to default production S3 bucket
  return 'https://mtkd-student-pictures.s3.amazonaws.com';
};

const API_CONFIG = {
  // Base API URL - can be overridden by environment variables
  BASE_URL: getApiBaseUrl(),
  
  // S3 Bucket URL - can be overridden by environment variables
  S3_BUCKET_URL: getS3BucketUrl(),
  
  // API Endpoints
  ENDPOINTS: {
    // Student endpoints
    STUDENTS: '/students',
    STUDENT_DETAIL: (id) => `/students/${id}`,
    STUDENT_GUARDIANS: (id) => `/students/${id}/guardians`,
    STUDENT_PROMOTIONS: (id) => `/students/${id}/promotions`,
    STUDENT_IMAGE: (id, type) => `/student_image/${id}/${type}`,
    
    // Guardian endpoints
    GUARDIANS: '/guardians',
    GUARDIAN_DETAIL: (id) => `/guardians/${id}`,
    GUARDIAN_SEARCH: (term) => `/guardians?search=${term}`,
    STUDENT_GUARDIAN_LINK: '/students/guardians',
    
    // Class endpoints
    CLASSES: '/classes',
    CLASSES_BY_DAY: (dayOfWeek) => `/classes/${dayOfWeek}`,
    CLASS_DETAIL: (id) => `/classes/${id}`,
    
    // Attendance endpoints
    ATTENDANCE: (classId, date) => `/attendance/${classId}/${date}`,
    ATTENDANCE_SAVE: (classId, date) => `/attendance/save/${classId}/${date}`,
    
    // Student roles endpoints
    STUDENT_ROLES: '/student-roles',
    STUDENT_ROLE_DETAIL: (id) => `/student-roles/${id}`,
    
    // Event endpoints
    EVENTS: '/events',
    EVENT_DETAIL: (id) => `/events/${id}`,
    EVENT_REGISTRATIONS: (eventId) => `/events/${eventId}/registrations`,
    EVENT_REGISTRATIONS_SAVE: '/events/registrations',
    EVENT_REGISTRATION_DELETE: (registrationId) => `/events/registrations/${registrationId}`,
    EVENT_TYPES: '/event-types',
    
    // Rank endpoints
    RANKS: '/ranks',
    
    // Reporting endpoints
    REPORTING: {
      STUDENTS_BY_RANK: '/reporting/students-by-rank',
      STUDENTS_BY_START_DATE: '/reporting/students-by-start-date',
      CLASS_ATTENDANCE: '/reporting/class-attendance',
      UNIQUE_STUDENTS_BY_DAY: '/reporting/unique-students-by-day'
    },
    
    // Finance endpoints
    FINANCE: '/finance',
    DEBT_TYPES: '/debt_types',
    DEBT_TYPE_DETAIL: (id) => `/debt_types/${id}`,
    STUDENT_DEBTS: '/student_debts',
    STUDENT_DEBT_DETAIL: (id) => `/student_debts/${id}`,
    PAYMENTS: '/payments',
    PAYMENT_HISTORY: '/payment-history',
    
    // Bulk upload endpoints
    BULK_UPSERT_STUDENTS: '/bulk/student/json',
    
    // Student image endpoints
    STUDENT_IMAGE: (id, type) => `/student_image/${id}/${type}`,
    STUDENT_IMAGES: '/students/images',
    STUDENT_IMAGES_RESIZE: '/students/images/resize',
    DELETE_ORPHANED_IMAGE: (imageGuid) => `/images/${imageGuid}`,
    
    // Event bulk operations
    EVENT_BULK: (eventId) => `/events/${eventId}/bulk`
  }
};

// Helper function to build full API URLs
export const buildApiUrl = (endpoint) => {
  if (typeof endpoint === 'function') {
    throw new Error('buildApiUrl requires a string endpoint. Use buildApiUrlWithParams for function endpoints.');
  }
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to build full API URLs with parameters
export const buildApiUrlWithParams = (endpointFunction, ...params) => {
  const endpoint = endpointFunction(...params);
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to build S3 URLs for student images
export const buildS3Url = (imageGuid) => {
  // Debug logging to help troubleshoot
  console.log('buildS3Url called with:', { imageGuid, type: typeof imageGuid });
  console.log('getS3BucketUrl called with:', { baseUrl: process.env.REACT_APP_API_BASE_URL });

  
  if (!imageGuid || imageGuid.trim() === '') {
    return null;
  }
  
  const url = `${API_CONFIG.S3_BUCKET_URL}/${imageGuid}`;
  return url;
};

// Helper function to get S3 bucket URL
export const getS3BucketUrlConfig = () => {
  return API_CONFIG.S3_BUCKET_URL;
};

// Export the configuration
export default API_CONFIG;
